import {useEffect,useState,useMemo} from 'react'
import {useDispatch,useSelector} from 'react-redux'
import styles from './Rooms.module.css'
import {
	getChatRooms,
	getChatRoom,
	selectRoomsStatus,
	selectRoomsCollapse,
	selectFilteredRoomsByType,
	selectActiveRoom,
	selectFilters,
	selectShow,
	setMode,
	setShow,
	setRoomsCollapse,
} from '../messageCenterSlice'

import {useHasCRM} from '../../Growth/hooks'

import CenteredLoader from '../../CenteredLoader/CenteredLoader'
import {capitalize} from '../../../util/util'
import Badge from 'react-bootstrap/Badge'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Button from 'react-bootstrap/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import useCurrentUser from '../../../hooks/useCurrentUser'

const UserIcon = ({user,size,margin,fontSize,background,borderRadius,boxShadow}) =>(
	Boolean(user?.profile_pic)?
		<img 
			onError={e=>{
				e.target.src="https://cdn.fitsw.com/assets/img/defaultProfile.png"
			}}
			style={{
				width:`${size}px`,
				height:`${size}px`,
				margin:`${margin}px`,
				objectFit:'cover',
				borderRadius
			}} 
			src={user?.profile_pic}
			alt="profile pic"
		/>
	:
	<div 
		className="d-flex align-items-center justify-content-center"
		style={{
			width:`${size}px`,
			height:`${size}px`,
			margin:`${margin}px`,
			background,
			display:'inline-block',
			flex:'0 0 auto',
			borderRadius,
			boxShadow,
			fontSize
		}}
	>
		{capitalize(user.user_name.charAt(0))}
	</div>
)

const Bubble = ({room,collapse,newMessageCount})=>{
	const {id:activeRoomId} = useSelector(selectActiveRoom)
	const isActive = room.id === activeRoomId
	const user = useSelector(state=>state.users.current_user)
	const isClient = user.account_type === 'client'

	if(
		//room has more than one other 
		room.others.length > 1 && 
		//trainers in shared rooms should only see
		//the client
		(
			!room.shared ||
			isClient
		)
	){
		const othersToDisplay = room.others.slice(0,4)
		const numNotDisplayed = room.others.length - othersToDisplay.length
		const nearestSquare = Math.ceil(Math.sqrt(othersToDisplay.length))
		const pxVal = 35/nearestSquare
		const colors = ['#338eff','#53db5a','#ffa733','#ffcc33']
		return (
			<div style={{
				flex:'0 0 auto',
				display:'grid',
				width:'35px',
				height:'35px',
				borderRadius:'100%',
				overflow:'hidden',
				margin:'2px',
				background:'#ccc',
				position:'relative',
				gridTemplateRows:`repeat(${nearestSquare},1fr)`,
				gridTemplateColumns:`repeat(${nearestSquare},1fr)`,
				boxShadow:isActive?'0 0 0 1px #fff':'none'
			}}>
				{(newMessageCount>0&&collapse)&&
					<div className="bg-primary" style={{
						position:'absolute',
						top:'3px',
						right:'3px',
						width:'9px',
						height:'9px',
						borderRadius:'100%',
						border:'1px solid #fff'
					}}/>
				}
				{othersToDisplay.map((other,i)=>(
					<UserIcon 
						user={room.members_map[other]} 
						size={pxVal} 
						margin={0}
						fontSize={room.others.length>1?11:16}
						background={colors[i]}
						borderRadius={0}
					/>
				))}
				{numNotDisplayed>0&&
					<div style={{
						position:'absolute',
						width:'18px',
						height:'18px',
						background:'#1f1f1f',
						color:'#ccc',
						fontSize:'14px',
						borderRadius:'100%',
						top:'50%',
						left:'50%',
						transform:'translate(-50%,-50%)',
						display:'flex',
						alignItems:'center',
						justifyContent:'center'
					}}>
						{room.others.length}
					</div>
				}
			</div>
		)
	}else if(room.others.length == 0){
		return (
			<div 
				className="d-flex align-items-center justify-content-center"
				style={{
					width:'35px',
					height:'35px',
					margin:'2px',
					background:'#338eff',
					display:'inline-block',
					flex:'0 0 auto',
					borderRadius:'50%'
				}}
			>
				<i className="fas fa-user-times"></i>
			</div>
		)
	}else{
		const otherUser = room.other || room.members_map[room.others[0]]
		return (
			<div style={{position:'relative'}}>
				{(newMessageCount>0&&collapse)&&
					<div className="bg-primary" style={{
						position:'absolute',
						top:'3px',
						right:'3px',
						width:'9px',
						height:'9px',
						borderRadius:'100%',
						border:'1px solid #fff'
					}}/>
				}
				<UserIcon 
					borderRadius="100%" 
					background='#338eff' 
					boxShadow={isActive?'0 0 0 1px #fff':'none'}
					size={35} 
					user={otherUser} margin={2} fontSize={16}/>
			</div>
		)
	}
}

const Names = ({room,collapse})=>{
	const {id:activeRoomId} = useSelector(selectActiveRoom)
	const user = useSelector(state=>state.users.current_user)
	const isClient = user.account_type === 'client'
	//if collapsed don't show name
	if(collapse) return null
	const isActive = room.id === activeRoomId
	const resolveNamesContent = ()=>{
		if(Boolean(room.name)){
			return room.name
		}else if(room.others.length===0){
			return 'No other members'
		}else if(room.others.length===1||room.shared){
			if(room.shared){
				if(isClient){
					const otherUser = room.members_map[room.others[0]]
					if(room.others.length>1){
						return 'Your Trainers'
					}else{
						return otherUser?.user_name
					}
				}else{
					const otherUser = room.other
					return `${otherUser?.user_name} (shared)`
				}
			}else{
				const otherUser = room.members_map[room.others[0]]
				return otherUser?.user_name
			}
		}else {
			return room.others.map(otherId=>(
				room.members_map[otherId].user_name.split(' ')[0]
			)).join(', ')
		}
	}
	return (
		<div className="ml-1" style={{
			textOverflow:'ellipsis',
			overflow:'hidden',
			whiteSpace:'nowrap',
			flex:'1 1 auto',
			color:isActive?'#fff':'#000'
		}}>
			{resolveNamesContent()}
		</div>
	)
}

const Room = ({room})=>{
	const {id:activeRoomId} = useSelector(selectActiveRoom)
	const isActive = room.id === activeRoomId
	const dispatch = useDispatch()
	const collapse = useSelector(selectRoomsCollapse)
	const user = useSelector(state=>state.users.current_user)
	//other user in private room
	const newMessageCount = room.members_map[user.user_id_number].new_message_count
	return (
		<div 
			onClick={()=>{
				dispatch(getChatRoom({
					room_id:room.id
				}))
			}}
			className="d-flex align-items-center"
			style={{
				cursor:'pointer',
				overflow:'hidden',
				width:'100%', 
				margin:'3px 0px 3px',
				background:isActive?'#338eff':'#fff'
			}}
		>
			<Bubble room={room} collapse={collapse} newMessageCount={newMessageCount}/>
			<Names room={room} collapse={collapse}/>
			{newMessageCount>0&&
				<Badge className="ml-auto mr-1" variant="primary">
					{newMessageCount}
				</Badge>
			}
		</div>
	)
}

const RoomGroup = ({header,rooms,type,collapse})=>{
	const user = useCurrentUser()
	const dispatch = useDispatch()
	const [open,setOpen] = useState(true)
	const isClient = useSelector( state => state.users.current_user.account_type === 'client')
	const [sortBy,setSortBy] = useState('az')

	const sortComparer = (a,b)=>{
		const userNewMessageCountA = a.members_map[user.user_id_number].new_message_count
		const userNewMessageCountB = b.members_map[user.user_id_number].new_message_count

		const getRoomAZSortString = (room)=>{
			//returns zzzzzz to force unamed to end
			if (type==='custom') { 
				if(room.name === "") return "zzzzzzzzzzzzz"
				return room.name.toLowerCase()
			}
			return  Boolean(room.other) ? room.other.user_name.toLowerCase() : "zzzzzzzzzzzzz"
		}

		const aValAZ = getRoomAZSortString(a)
		const bValAZ = getRoomAZSortString(b)
			
		const aValNew = (
			//force chat rooms with no messages to the bottom
			a.last_message_received_at === ""
				? -1
				: (new Date(a.last_message_received_at?.split(' ').join('T')).getTime())
		)
		const bValNew = (
			//force chat rooms with no messages to the bottom
			b.last_message_received_at === ""
				? -1
				: new Date(b.last_message_received_at?.split(' ').join('T')).getTime()
		)
		
		if(sortBy==='az'){
			return aValAZ <= bValAZ ? -1 : 1
		}else{
			//multi-tiered sort
			//Forces unread messages to top
			//then sorts by newest recieved message
			//then sorts alphabetically
			return userNewMessageCountB - userNewMessageCountA || bValNew - aValNew || (aValAZ <= bValAZ ? -1 : 1)
		}
	}

	const sortedRooms = useMemo(()=>rooms.sort(sortComparer),[sortBy,rooms])

	return (
		<div>
			<div className={styles.RoomTypeHeader} onClick={()=>{setOpen(!open)}}>
				{header}
			</div>
			{/* Additional sort functionality for client chat room list*/}
			{ type === 'client' &&
				<div className="text-center">
					<small className="d-block">Sort:</small>
					<ButtonGroup size="sm" vertical={collapse}>
						<OverlayTrigger overlay={
							<Tooltip>Alphabetically</Tooltip>
						}>
							<Button 
								variant={sortBy==='az'?'primary':'outline-primary'}
								onClick={()=>{ setSortBy('az') }}
							>
								A-Z
							</Button>
						</OverlayTrigger>
						<OverlayTrigger overlay={
							<Tooltip>Recent messages first</Tooltip>
						}>
							<Button 
								variant={sortBy==='recent'?'primary':'outline-primary'}
								onClick={()=>{ setSortBy('recent') }}
							>
								New
							</Button>
						</OverlayTrigger>
					</ButtonGroup>
				</div>
			}
			<div className={[
				styles.RoomGroupList,
				!open&&styles.Close
			].join(' ')}>
				{(type==='custom'&&!isClient)&&
					<div className="p-1">
						<OverlayTrigger overlay={
							<Tooltip>
								Create a group chat room
							</Tooltip>
						}>
							<Button
								onClick={()=>{
									dispatch(setMode('CREATE_ROOM'))
								}}
								variant="outline-success"
								size="sm"
								block
							>
								<i style={{fontSize:'12px'}} className="fa fa-plus"/>
							</Button>
						</OverlayTrigger>
					</div>
				}
				{
					sortedRooms.map( room => (
						<Room key={room.id} room={room} />
					))
				}
			</div>
		</div>
	)
}

const Rooms = ()=>{
	const dispatch = useDispatch()
	const user = useSelector(state=>state.users.current_user)
	const {newOnly,query} = useSelector(selectFilters)
	const collapse = useSelector(selectRoomsCollapse)
	const roomsStatus = useSelector(selectRoomsStatus)
	const roomsByType = useSelector(selectFilteredRoomsByType)
	const hasCRM = useHasCRM()
	const roomTypeToSortVal = t=>({
		custom:0,
		gym:1,
		trainer:2,
		client:3,
		prospect:4
	}[t])
	const roomTypeToHeader = (t,numRooms=0) => {
		if(t === 'custom'){
			return collapse ? <i className="fa fa-users"/> :'Group Messages'
		}else{
			const headerString = `${capitalize(t)}${numRooms>1?'s':''}`
			return collapse ? headerString.charAt(0) : headerString
		}
	}
	const roomsByTypeEntries = Object.entries(roomsByType).sort((e1,e2)=>(
		roomTypeToSortVal(e1[0]) - roomTypeToSortVal(e2[0])
	))

	//if the only room group is the empty custom group
	const noResults = roomsByTypeEntries?.length === 1 && roomsByTypeEntries[0][1]?.length === 0
	const isFiltering = Boolean(query) || newOnly
	useEffect(()=>{
		dispatch(getChatRooms())
	},[dispatch])
	return (
		<>
		<div style={{position:"relative"}}>
			<button
				onClick={()=>{dispatch(setRoomsCollapse(!collapse))}}
				className={styles.expandButton}
			>{collapse ? <i className="fa fa-angle-double-right"/> :  <i className="fa fa-angle-double-left"/>}</button>
			{/* <i class="far fa-angle-double-left"></i> */}
			<div className={[
				styles.Rooms,
				collapse&&styles.Collapse
			].join(' ')}>
				{
					roomsStatus==='pending'?
					<CenteredLoader/>:
					<>
						{(roomsByType&&Boolean(roomsByTypeEntries))&&
							roomsByTypeEntries.map(([roomType,rooms])=>(
								(roomType!=='prospect'||hasCRM)&&
								<RoomGroup 
									type={roomType}
									header={roomTypeToHeader(roomType,rooms.length)}
									rooms={rooms}
									collapse={collapse}
								/>
							))
						}
						{(roomsByType&&noResults)&&
							<div className="d-flex justify-content-center align-items-center">
								{
									collapse ?
									<div style={{
										fontSize:'10px',
										textAlign:'center',
										writingMode: 'vertical-rl',
										textOrientation: 'upright'
									}}>
										{
											isFiltering?
											(
												newOnly?
												"0 NEW" :
												"0 MATCHES"
											)
											:
											"0 Contacts"
										}
									</div>
									:
									<span className="text-center p-3">
										{
											isFiltering ?
											(
												newOnly ?
												`No rooms with new messages${
													Boolean(query)?
													` matching "${query}"`:
													''
												}`:
												`No matches for "${query}"`
											)
											:
											"No Contacts"
										}
									</span>
								}
							</div>
						}
					</>
				}
			</div>
			<div className={styles.Placeholder}/>
		</div>
		</>
	)
}

export default Rooms